import React, { useCallback, useEffect, useMemo } from 'react';

import {
    calculateHourMinFormat,
    sortableHeader
} from 'pages/reports/components/ReportFunctions';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { createSortChangeHandler, GridSortChangeEvent } from 'pages/reports/components/sortUtils';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { buildExportFileName } from 'utils';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/Report74/Report74.scss';

interface Report74Props {
    mode?: string;
    reportSections: ReportSection[];
}

interface ObjectiveOption {
    name: string;
    value: string;
}

interface QuestionRow {
    attained: number;
    avgAttemptsPerStudent: number;
    avgTimePerAttemptMinutes: number;
    displayName: string;
    objectives: string[];
    QuestionId: number;
    recentCorrectness: number;
    studentCoverage: number;
    studentsAttempted: number;
    totalAttempts: number;
    totalTimeSpentMinutes: number;
}

interface SummaryMetrics {
    avgAttained: number;
    avgAttemptsPerQuestion: number;
    avgRecentCorrectness: number;
    avgStudentCoverage: number;
    avgTimePerQuestionMinutes: number;
    totalQuestions: number;
}

const MAX_DISPLAY_ROWS = 500;

export const Report74 = ({
    mode = 'Testing',
    reportSections
}: Report74Props) => {

    const [state, setState] = useSuperState({
        allQuestionRows: [] as QuestionRow[],
        exportFileName: 'QuestionOverviewReport.csv',
        exportRows: [] as any[],
        objectiveOptions: [] as ObjectiveOption[],
        selectedObjectives: [] as ObjectiveOption[],
        sort: [] as SortDescriptor[],
        summaryMetrics: {
            avgAttained: 0,
            avgAttemptsPerQuestion: 0,
            avgRecentCorrectness: 0,
            avgStudentCoverage: 0,
            avgTimePerQuestionMinutes: 0,
            totalQuestions: 0
        } as SummaryMetrics
    });

    const { allQuestionRows, exportFileName, exportRows, objectiveOptions, selectedObjectives, sort, summaryMetrics } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // Filter rows based on selected objectives (visual filter only — doesn't affect summary)
    const filteredRows = useMemo(() => {

        if (objectiveOptions.length === 0) return allQuestionRows;
        if (selectedObjectives.length === 0) return [];
        if (selectedObjectives.length === objectiveOptions.length) return allQuestionRows;
        const selectedValues = new Set(selectedObjectives.map(o => o.value));
        return allQuestionRows.filter(row => row.objectives.some(obj => selectedValues.has(obj))
        );

    }, [allQuestionRows, selectedObjectives, objectiveOptions]);

    const sortedData = useMemo(() => orderBy(filteredRows, sort), [filteredRows, sort]);
    const displayData = useMemo(() => sortedData.slice(0, MAX_DISPLAY_ROWS), [sortedData]);

    // Aggregate raw question-attempt data into per-question rows and summary metrics
    const aggregateData = useCallback((data: any[]) => {

        const allQuestionIds = new Set(data.map((d: any) => d.QuestionId));
        const totalQuestionsInScope = allQuestionIds.size;

        // Determine total students in scope
        const allStudentIds = new Set(data.map((d: any) => d.StudentId));
        const totalStudentsInScope = allStudentIds.size;

        // Group attempts by question
        const questionMap: Record<number, any[]> = {};
        data.forEach((item: any) => {

            const qId = item.QuestionId;
            if (!questionMap[qId]) questionMap[qId] = [];
            questionMap[qId].push(item);

        });

        const questionRows: QuestionRow[] = Object.entries(questionMap).map(([, attempts]) => {

            const first = attempts[0];
            const questionName = first.QuestionName || `Question ${first.QuestionId}`;
            const projectName = first.ProjectName;
            const displayName = projectName
                ? `${projectName} ${questionName} (${first.QuestionId})`
                : `${questionName} (${first.QuestionId})`;

            // Collect objectives for this question (for filtering)
            const objSet = new Set<string>();
            attempts.forEach((a: any) => {

                if (a.ObjectiveName) objSet.add(a.ObjectiveName);

            });

            // Students who attempted this question
            const studentsWhoAttempted = new Set(attempts.map((a: any) => a.StudentId));
            const studentsAttempted = studentsWhoAttempted.size;

            // Student Coverage: % of students in scope who attempted this question
            const studentCoverage = totalStudentsInScope > 0
                ? (studentsAttempted / totalStudentsInScope) * 100
                : 0;

            // Attained: % of students in scope who answered correctly at least once
            const studentsCorrect = new Set(
                attempts.filter((a: any) => a.Correct?.Share === '1').map((a: any) => a.StudentId)
            );
            const attained = totalStudentsInScope > 0
                ? (studentsCorrect.size / totalStudentsInScope) * 100
                : 0;

            // Recent Correctness: % of students who attempted whose most recent attempt was correct
            // Group by student, find most recent attempt per student, check if correct
            const studentLatestMap: Record<number, any> = {};
            attempts.forEach((a: any) => {

                const sId = a.StudentId;
                if (!studentLatestMap[sId] || new Date(a.TimeStamp) > new Date(studentLatestMap[sId].TimeStamp)) {

                    studentLatestMap[sId] = a;

                }

            });
            const recentCorrectCount = Object.values(studentLatestMap)
                .filter((a: any) => a.Correct?.Share === '1').length;
            const recentCorrectness = studentsAttempted > 0
                ? (recentCorrectCount / studentsAttempted) * 100
                : 0;

            // Total attempts
            const totalAttempts = attempts.length;

            // Average Attempts / Student (only students who attempted)
            const avgAttemptsPerStudent = studentsAttempted > 0
                ? totalAttempts / studentsAttempted
                : 0;

            // Time: deduplicate by ContentAccessId, then sum Testtotalminutesspent
            const seenAccessIds = new Set<number>();
            let totalTimeSpentMinutes = 0;
            attempts.forEach((a: any) => {

                if (a.ContentAccessId && !seenAccessIds.has(a.ContentAccessId)) {

                    seenAccessIds.add(a.ContentAccessId);
                    totalTimeSpentMinutes += a.Testtotalminutesspent || 0;

                }

            });

            const avgTimePerAttemptMinutes = totalAttempts > 0
                ? totalTimeSpentMinutes / totalAttempts
                : 0;

            return {
                attained,
                avgAttemptsPerStudent,
                avgTimePerAttemptMinutes,
                displayName,
                objectives: Array.from(objSet),
                QuestionId: first.QuestionId,
                recentCorrectness,
                studentCoverage,
                studentsAttempted,
                totalAttempts,
                totalTimeSpentMinutes
            };

        });

        // Build objective options from all data
        const objMap = new Map<string, string>();
        data.forEach((item: any) => {

            if (item.ObjectiveName) {

                objMap.set(item.ObjectiveName, item.ObjectiveName);

            }

        });
        const options: ObjectiveOption[] = Array.from(objMap.entries())
            .map(([name]) => ({ name, value: name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        setState({ objectiveOptions: options });
        setState({ selectedObjectives: options }); // Default: all selected

        // Summary metrics (always from full dataset, not filtered by objectives)
        // Per the PRD, summary metrics are per-student averages then averaged across students

        // Group attempts by student for summary calculations
        const studentMap: Record<number, any[]> = {};
        data.forEach((item: any) => {

            const sId = item.StudentId;
            if (!studentMap[sId]) studentMap[sId] = [];
            studentMap[sId].push(item);

        });

        const studentEntries = Object.values(studentMap);
        const studentCount = studentEntries.length;

        // Avg Question Coverage: per student (unique questions attempted / total questions), then average
        const avgStudentCoverage = studentCount > 0
            ? studentEntries.reduce((sum, studentAttempts) => {

                const uniqueQs = new Set(studentAttempts.map((a: any) => a.QuestionId));
                return sum + (totalQuestionsInScope > 0 ? (uniqueQs.size / totalQuestionsInScope) * 100 : 0);

            }, 0) / studentCount
            : 0;

        // Avg Attained: per student (questions answered correctly at least once / total questions), then average
        const avgAttained = studentCount > 0
            ? studentEntries.reduce((sum, studentAttempts) => {

                const correctQs = new Set(
                    studentAttempts.filter((a: any) => a.Correct?.Share === '1').map((a: any) => a.QuestionId)
                );
                return sum + (totalQuestionsInScope > 0 ? (correctQs.size / totalQuestionsInScope) * 100 : 0);

            }, 0) / studentCount
            : 0;

        // Avg Recent: per student (questions whose most recent score was correct / total questions), then average
        const avgRecentCorrectness = studentCount > 0
            ? studentEntries.reduce((sum, studentAttempts) => {

                const qLatest: Record<number, any> = {};
                studentAttempts.forEach((a: any) => {

                    const qId = a.QuestionId;
                    if (!qLatest[qId] || new Date(a.TimeStamp) > new Date(qLatest[qId].TimeStamp)) {

                        qLatest[qId] = a;

                    }

                });
                const recentCorrect = Object.values(qLatest).filter((a: any) => a.Correct?.Share === '1').length;
                return sum + (totalQuestionsInScope > 0 ? (recentCorrect / totalQuestionsInScope) * 100 : 0);

            }, 0) / studentCount
            : 0;

        // Avg Attempts / Question: per student (total attempts / total questions), then average
        const avgAttemptsPerQuestion = studentCount > 0
            ? studentEntries.reduce((sum, studentAttempts) => sum + (totalQuestionsInScope > 0 ? studentAttempts.length / totalQuestionsInScope : 0), 0) / studentCount
            : 0;

        // Avg Time / Question: per student (total time of most recent attempt / unique questions attempted), then average
        const avgTimePerQuestionMinutes = studentCount > 0
            ? studentEntries.reduce((sum, studentAttempts) => {

                // Deduplicate by ContentAccessId for time
                const seenIds = new Set<number>();
                let studentTime = 0;
                studentAttempts.forEach((a: any) => {

                    if (a.ContentAccessId && !seenIds.has(a.ContentAccessId)) {

                        seenIds.add(a.ContentAccessId);
                        studentTime += a.Testtotalminutesspent || 0;

                    }

                });
                const uniqueQs = new Set(studentAttempts.map((a: any) => a.QuestionId));
                return sum + (uniqueQs.size > 0 ? studentTime / uniqueQs.size : 0);

            }, 0) / studentCount
            : 0;

        setState({
            summaryMetrics: {
                avgAttained,
                avgAttemptsPerQuestion,
                avgRecentCorrectness,
                avgStudentCoverage,
                avgTimePerQuestionMinutes,
                totalQuestions: totalQuestionsInScope
            }
        });
        setState({ allQuestionRows: questionRows });

    }, []);

    // Sorting
    const getSortableValue = useCallback((item: any, field?: string) => {

        if (!field) return null;
        return item[field];

    }, []);

    const handleSortChange = useCallback((event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    }, [sort, getSortableValue]);

    // Export
    const buildExport = useCallback(() => sortedData.map((row: QuestionRow) => ({

        [formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' })]: `${Math.round(row.attained)}%`,
        [formatMessage({ id: 'avg_attempts_student.f3df6812', defaultMessage: 'Avg Attempts / Student' })]: row.avgAttemptsPerStudent.toFixed(1),
        [formatMessage({ id: 'avg_time_attempt.c6fd1ca6', defaultMessage: 'Avg Time / Attempt' })]: calculateHourMinFormat(Math.round(row.avgTimePerAttemptMinutes)),
        [formatMessage({ id: 'question_name.b3f17db0', defaultMessage: 'Question Name' })]: row.displayName,
        [formatMessage({ id: 'recent_correctness.2e3a499a', defaultMessage: 'Recent Correctness' })]: `${Math.round(row.recentCorrectness)}%`,
        [formatMessage({ id: 'student_coverage.92274580', defaultMessage: 'Student Coverage' })]: `${Math.round(row.studentCoverage)}%`,
        [formatMessage({ id: 'students_attempted.1e3bc45f', defaultMessage: 'Students Attempted' })]: row.studentsAttempted,
        [formatMessage({ id: 'total_attempts.0d55f966', defaultMessage: 'Total Attempts' })]: row.totalAttempts,
        [formatMessage({ id: 'total_time_spent.88b400d6', defaultMessage: 'Total Time Spent' })]: calculateHourMinFormat(Math.round(row.totalTimeSpentMinutes))

    })), [sortedData, formatMessage]);

    const handleExportCSV = useCallback(() => {

        const reportName = reportSections[0]?.Title || 'QuestionOverviewReport';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        setState({ exportRows: buildExport() });
        setTimeout(() => document.getElementById('csvLink74')?.click(), 0);

    }, [reportSections, buildExport]);

    // Initialize on mount
    useEffect(() => {

        const allData = reportSections[0]?.Components[0]?.Results?.Data ?? [];
        const rawData = allData.filter((d: any) => !d.ContentAccessTypeDisplayName || d.ContentAccessTypeDisplayName === mode);
        aggregateData(rawData);

    }, [aggregateData, reportSections, mode]);

    // Toast for truncated results
    useEffect(() => {

        if (filteredRows.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: filteredRows.length }
                ),
                'warning'
            );

        }

    }, [filteredRows]);

    // Summary card definitions
    const summaryCards = useMemo(() => [
        {
            label: formatMessage({ id: 'total_questions.98fad60f', defaultMessage: 'Total Questions' }),
            tooltip: formatMessage({ id: 'total_number_of_unique_questions_include.732a1459', defaultMessage: 'Total number of unique questions included within the selected scope' }),
            value: summaryMetrics.totalQuestions.toString()
        },
        {
            label: formatMessage({ id: 'avg_student_coverage.ea366550', defaultMessage: 'Avg Student Coverage' }),
            tooltip: formatMessage({ id: 'average_percentage_of_students_who_attem.d46b1b20', defaultMessage: 'Average percentage of students who attempted each question at least once' }),
            value: `${Math.round(summaryMetrics.avgStudentCoverage)}%`
        },
        {
            label: formatMessage({ id: 'avg_attained.8c0a67d4', defaultMessage: 'Avg Attained' }),
            tooltip: formatMessage({ id: 'average_percentage_of_students_who_answe.1c7f9ac0', defaultMessage: 'Average percentage of students who answered each question correctly at least once' }),
            value: `${Math.round(summaryMetrics.avgAttained)}%`
        },
        {
            label: formatMessage({ id: 'avg_recent_correctness.7d68154c', defaultMessage: 'Avg Recent Correctness' }),
            tooltip: formatMessage({ id: 'average_percentage_of_students_whose_mos.8926a0f9', defaultMessage: 'Average percentage of students whose most recent submitted attempt for each question was correct' }),
            value: `${Math.round(summaryMetrics.avgRecentCorrectness)}%`
        },
        {
            label: formatMessage({ id: 'avg_attempts_question.64637c60', defaultMessage: 'Avg Attempts / Question' }),
            tooltip: formatMessage({ id: 'average_number_of_submitted_attempts_per.451c2944', defaultMessage: 'Average number of submitted attempts per question within the selected scope' }),
            value: summaryMetrics.avgAttemptsPerQuestion.toFixed(1)
        },
        {
            label: formatMessage({ id: 'avg_time_question.1a5ce3ba', defaultMessage: 'Avg Time / Question' }),
            tooltip: formatMessage({ id: 'average_time_spent_per_question_within_t.e9f25446', defaultMessage: 'Average time spent per question within the selected scope' }),
            value: calculateHourMinFormat(Math.round(summaryMetrics.avgTimePerQuestionMinutes))
        }
    ], [summaryMetrics, formatMessage]);

    return (
        <>
            {/* Header Summary — Question Overview */}
            <Container fluid className="px-0 mb-4">
                <h5 className="fw-bold mb-3">
                    <FormattedMessage defaultMessage="Question Overview" id="question_overview.494a001d" />
                </h5>
                <Row className="g-3 mx-0">
                    {summaryCards.map(metric => (
                        <Col key={metric.label} lg="2" md="4" sm="4" xs="6">
                            <Card className="p-3 shadow-sm h-100 text-center">
                                <ReportToolTip id={`summary-${metric.label}`} tooltip={metric.tooltip} trigger="hover">
                                    <div className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                        {metric.label}
                                    </div>
                                    <div className="fw-bold fs-3">
                                        {metric.value}
                                    </div>
                                </ReportToolTip>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Table */}
            <Card>
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink74"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <div className="d-flex">
                        {objectiveOptions.length > 0 && (
                            <GMetrixDropdown
                                filterable
                                multiSelect
                                dataItemKey="value"
                                dataItems={objectiveOptions}
                                label={formatMessage({ id: 'show_questions_for_objectives.77d12c2f', defaultMessage: 'Show Questions For Objectives' })}
                                onChange={e => setState({ selectedObjectives: e as ObjectiveOption[] })}
                                style={{ maxWidth: '300px' }}
                                textField="name"
                                value={selectedObjectives}
                            />
                        )}
                    </div>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover className="kendo-table sticky-table-header mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-start"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'displayName' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 280, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-question" tooltip={formatMessage({ id: 'question_included_within_the_selected_te.701137f2', defaultMessage: 'Question included within the selected Test(s)' })} trigger="hover">
                                            <span className="d-flex justify-content-start">
                                                <FormattedMessage defaultMessage="Question Name" id="question_name.b3f17db0" />
                                            </span>
                                        </ReportToolTip>,
                                        'displayName',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'studentCoverage' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-coverage" tooltip={formatMessage({ id: 'percent_of_students_in_scope_who_submitt.75ab2c02', defaultMessage: 'Percent of students in scope who submitted at least one attempt for this question' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Student Coverage" id="student_coverage.92274580" />
                                            </span>
                                        </ReportToolTip>,
                                        'studentCoverage',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'studentsAttempted' }] })}
                                    style={{ cursor: 'pointer', minWidth: 160 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-studentsAttempted" tooltip={formatMessage({ id: 'number_of_students_in_scope_who_submitte.920fb24b', defaultMessage: 'Number of students in scope who submitted at least one attempt for this question' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Students Attempted" id="students_attempted.1e3bc45f" />
                                            </span>
                                        </ReportToolTip>,
                                        'studentsAttempted',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'attained' }] })}
                                    style={{ cursor: 'pointer', minWidth: 120 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-attained" tooltip={formatMessage({ id: 'percent_of_students_who_answered_this_qu.77945378', defaultMessage: 'Percent of students who answered this question correctly at least once within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Attained" id="attained.704661b2" />
                                            </span>
                                        </ReportToolTip>,
                                        'attained',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'recentCorrectness' }] })}
                                    style={{ cursor: 'pointer', minWidth: 170 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-recent" tooltip={formatMessage({ id: 'percent_of_students_whose_most_recent_su.c9e16301', defaultMessage: 'Percent of students whose most recent submitted attempt for this question was correct within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Recent Correctness" id="recent_correctness.2e3a499a" />
                                            </span>
                                        </ReportToolTip>,
                                        'recentCorrectness',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgAttemptsPerStudent' }] })}
                                    style={{ cursor: 'pointer', minWidth: 180 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-avgAttempts" tooltip={formatMessage({ id: 'average_number_of_submitted_attempts_per.653ba8b3', defaultMessage: 'Average number of submitted attempts per student who attempted this question' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Avg Attempts / Student" id="avg_attempts_student.f3df6812" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgAttemptsPerStudent',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalAttempts' }] })}
                                    style={{ cursor: 'pointer', minWidth: 140 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-totalAttempts" tooltip={formatMessage({ id: 'total_number_of_submitted_attempts_for_t.a9cad053', defaultMessage: 'Total number of submitted attempts for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Attempts" id="total_attempts.0d55f966" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalAttempts',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgTimePerAttemptMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 160 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-avgTime" tooltip={formatMessage({ id: 'average_time_spent_per_submitted_attempt.e2c12775', defaultMessage: 'Average time spent per submitted attempt on this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Avg Time / Attempt" id="avg_time_attempt.c6fd1ca6" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgTimePerAttemptMinutes',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalTimeSpentMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-totalTime" tooltip={formatMessage({ id: 'total_time_spent_across_all_submitted_at.c9d501f0', defaultMessage: 'Total time spent across all submitted attempts for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Time Spent" id="total_time_spent.88b400d6" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalTimeSpentMinutes',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((question: QuestionRow, rowIndex: number) => (
                                <tr key={`${question.QuestionId}-${rowIndex}`}>
                                    <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                        <ReportToolTip copyText={question.displayName} id={`q-${question.QuestionId}`} tooltip={question.displayName} trigger="hover">
                                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {question.displayName}
                                            </span>
                                        </ReportToolTip>
                                    </td>
                                    <td className="text-center">
                                        {Math.round(question.studentCoverage)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {question.studentsAttempted}
                                    </td>
                                    <td className="text-center">
                                        {Math.round(question.attained)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {Math.round(question.recentCorrectness)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {question.avgAttemptsPerStudent.toFixed(1)}
                                    </td>
                                    <td className="text-center">
                                        {question.totalAttempts}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(Math.round(question.avgTimePerAttemptMinutes))}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(Math.round(question.totalTimeSpentMinutes))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </>
    );

};